import { TransportLeg } from '../types';

// Kolkata Metro and transport connections
export const METRO_STATIONS: Record<string, { lat: number; lng: number; line: string }> = {
  'Sealdah': { lat: 22.5676, lng: 88.3547, line: 'North-South' },
  'Park Street': { lat: 22.5634, lng: 88.3697, line: 'North-South' },
  'Esplanade': { lat: 22.5656, lng: 88.3628, line: 'East-West' },
  'Rabindra Sarovar': { lat: 22.5442, lng: 88.3619, line: 'North-South' },
  'Kalighat': { lat: 22.5186, lng: 88.3631, line: 'North-South' },
  'Salt Lake Stadium': { lat: 22.5593, lng: 88.4408, line: 'East-West' },
  'College Street': { lat: 22.5597, lng: 88.3673, line: 'North-South' },
};

// Sample transport routes between major points
export const TRANSPORT_ROUTES: Record<string, TransportLeg[]> = {
  'Jadavpur-Sealdah': [
    {
      from: 'Jadavpur',
      to: 'Sealdah',
      mode: 'local_train',
      duration: 25,
      cost: 10,
      distance: 4.5,
      changes: 0,
      instructions: 'Take local train from Jadavpur towards Sealdah. Ask locals or check signage for the correct platform.',
      easyForCouples: true,
    }
  ],
  'Sealdah-Park Street': [
    {
      from: 'Sealdah',
      to: 'Park Street',
      mode: 'metro',
      duration: 8,
      cost: 15,
      distance: 1.2,
      changes: 0,
      instructions: 'Take North-South Metro Line towards Park Street. Exit at Park Street Station.',
      easyForCouples: true,
    }
  ],
  'Park Street-Victoria Memorial': [
    {
      from: 'Park Street',
      to: 'Victoria Memorial',
      mode: 'walking',
      duration: 12,
      cost: 0,
      distance: 0.8,
      changes: 0,
      instructions: 'Walk south through Park Street area. Follow signs towards Victoria Memorial.',
      easyForCouples: true,
    }
  ],
  'Park Street-Howrah': [
    {
      from: 'Park Street',
      to: 'Howrah',
      mode: 'metro',
      duration: 15,
      cost: 15,
      distance: 2.0,
      changes: 1,
      instructions: 'Take North-South Line to Sealdah, then change to East-West line (or walk to Howrah Bridge).',
      easyForCouples: false,
    }
  ],
  'Sealdah-Alipore Zoo': [
    {
      from: 'Sealdah',
      to: 'Alipore Zoo',
      mode: 'metro',
      duration: 12,
      cost: 15,
      distance: 1.8,
      changes: 0,
      instructions: 'Take North-South Metro towards Rabindra Sarovar. Walk from Rabindra Sarovar to zoo.',
      easyForCouples: true,
    }
  ],
};

export const SAMPLE_CONNECTIONS = {
  'walking-distance': {
    'Victoria Memorial': ['Alipore Zoo', 'Millennium Park'],
    'Park Street': ['College Street', 'Metcalfe Hall', 'Prinsep Ghat'],
    'Sealdah': ['New Market', 'Nakhoda Mosque'],
  },
  'metro-accessible': {
    'Park Street': ['Sealdah', 'Esplanade', 'Rabindra Sarovar'],
    'Kalighat': ['Rabindra Sarovar', 'Sealdah'],
    'Salt Lake Stadium': ['New Town'],
  },
  'local-train': {
    'Jadavpur': ['Sealdah', 'Howrah', 'Garia'],
    'Sealdah': ['Jadavpur', 'Tollygunge'],
  }
};

// Approximate travel time matrix between major areas
export const TRAVEL_TIMES: Record<string, Record<string, { metro: number; bus: number; walk: number }>> = {
  'Jadavpur': {
    'Sealdah': { metro: 30, bus: 40, walk: 999 },
    'Park Street': { metro: 40, bus: 50, walk: 999 },
    'Salt Lake': { metro: 35, bus: 45, walk: 999 },
  },
  'Sealdah': {
    'Park Street': { metro: 10, bus: 20, walk: 15 },
    'Victoria Memorial': { metro: 20, bus: 30, walk: 25 },
    'Alipore': { metro: 15, bus: 25, walk: 999 },
  },
  'Park Street': {
    'Howrah': { metro: 20, bus: 30, walk: 20 },
    'Prinsep Ghat': { metro: 5, bus: 10, walk: 10 },
    'College Street': { metro: 5, bus: 10, walk: 8 },
  }
};

// Calculate approximate distance between coordinates
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Estimate travel time based on distance and mode
export function estimateTravelTime(distance: number, mode: string): number {
  const speeds: Record<string, number> = {
    'walking': 4, // km/h
    'metro': 25, // km/h average with stops
    'local_train': 30, // km/h
    'bus': 15, // km/h in Kolkata traffic
    'auto': 20, // km/h
    'cab': 20, // km/h
    'ferry': 10, // km/h
  };
  const speed = speeds[mode] || 20;
  return Math.ceil((distance / speed) * 60);
}
